import React, {Component} from "react";

import {Col, Descriptions, Row, Spin, Statistic, Table} from "antd";
import DescriptionsItem from "antd/es/descriptions/Item";
import CheckableTag from "antd/es/tag/CheckableTag";
import {Pie} from "react-chartjs-2";

import api from "../api";
import {FullHeightCard} from "../components/helpers/cards/FullHeightCard";
import {Graph} from "../components/helpers/Graph";
import {FieldsNaming} from "../variants";
import {projectCurrency, properties} from "../properties";

function PieChart({data, field, option}) {
	const borders = ["rgba(0, 159, 152, 1)", "rgba(162,199,226, 1)"];
	const useColors = ["rgba(0, 159, 152, 0.2)", "rgba(162,199,226, 0.2)"];

	const getData = () => {
		const labels = data.includes.map(project => project.project_name);

		const dataValues = data.includes.map(project => project.statistics[option].numeric[field]);

		return {
			labels: labels,
			datasets: [
				{
					data: dataValues,
					backgroundColor: useColors,
					borderColor: borders,
					borderWidth: 1,
				},
			],
		};
	};

	const options = {
		plugins: {
			legend: {
				display: true,
				position: "bottom",
			},
		},
		interaction: false,
	};

	return <Pie data={getData()} style={{height: 200, maxHeight: 175}} options={options} />;
}

function DashboardRowv2({stat, tag}) {
	return (
		<Row>
			<Col span={24}>
				<Descriptions layout="vertical" title={tag} labelStyle={{fonSize: 14, fontWeight: "bold"}} bordered>
					<DescriptionsItem span={1} label={"Total"} contentStyle={{fontSize: 12}}>
						{stat["total"]}
					</DescriptionsItem>
					<DescriptionsItem span={1} label={"Stocked"} contentStyle={{fontSize: 12}}>
						{stat["stocked"]}
					</DescriptionsItem>
					<DescriptionsItem span={1} label={"Value"} contentStyle={{fontSize: 12}}>
						{stat["value"]}
					</DescriptionsItem>
				</Descriptions>
			</Col>
		</Row>
	);
}

function DashboardRowv3({stats, labels, selectedRow}) {
	const getLabel = option => labels[option] || option;
	const getRows = () => {
		const rows = [];
		for (const stat in stats) {
			rows.push({
				...stats[stat],
				label: getLabel(stat),
			});
		}

		return rows;
	};

	const columns = [
		{
			dataIndex: "label",
			title: "",
			width: 100,
			render: label => <div style={{fontWeight: "bold"}}>{label}</div>,
		},
		{
			dataIndex: "total",
			title: "Total",
			align: "center",
		},
		{
			dataIndex: "stocked",
			title: "Stocked",
			align: "center",
		},
		{
			dataIndex: "value",
			title: "Value",
			align: "center",
		},
	];

	const highlightSelectedRows = record => (record.label === getLabel(selectedRow) ? "selectedRowOption" : "");

	return (
		<div>
			<Table
				bordered={true}
				columns={columns}
				rowClassName={highlightSelectedRows}
				dataSource={getRows()}
				size={"small"}
				pagination={false}
			/>
		</div>
	);
}

export class AnalyticalDashboard extends Component {
	tagsData = () => [
		{value: "overall", label: "Overall"},
		{value: "max", label: "MAX > 0"},
		{value: "surplus_basic", label: `${FieldsNaming.overmax_name} (Basic)`},
		{value: "financial_obsoletes", label: "Financially Obsoletes"},
		{value: "io", label: "Inventory Optimisation"},
	];

	graphsData = () => [
		{value: "pie", label: "Charts"},
		// {value: "line", label: "Line Chart"},
	];

	constructor(props) {
		super(props);
		this.state = {
			statistics: {},
			selectedOption: this.tagsData()[0],
			selectedGraph: 0,
			loading: false,
			labels: {},
			statOptions: [],
			selectedStatOption: {},
			history: [],
			historyLoading: false,
		};
	}

	getHistory = (query = this.state.selectedOption) => {
		this.setState({historyLoading: true});
		api.get(`/projects/analytical-dashboard/${query.value}/history`)
			.then(data => {
				this.setState({
					history: data.data,
				});
			})
			.catch(err => {
				console.log(err);
			})
			.finally(() => {
				this.setState({historyLoading: false});
			});
	};

	componentDidUpdate(prevProps, prevState, snapshot) {
		if (prevState.selectedOption !== this.state.selectedOption) {
			this.getHistory(this.state.selectedOption);
		}
	}

	getStatistics(query = this.state.selectedOption) {
		this.setState({loading: true});
		api.get(`/projects/analytical-dashboard/${query.value}`)
			.then(data => {
				this.setState({
					statistics: data.data.data,
					selectedOption: query,
					labels: data.data.labels,
					selectedGraph: {value: "pie"},
					statOptions: Object.keys(data.data.data.statistics),
					selectedStatOption: Object.keys(data.data.data.statistics)[0],
				});
				this.getHistory(query);
			})
			.catch(err => {
				console.log(err);
			})
			.finally(() => {
				this.setState({loading: false});
			});
	}

	componentDidMount() {
		this.getStatistics();
	}

	beautifyDate = historyDate =>
		historyDate !== "NA" && new Date(historyDate)
			? new Date(historyDate).toLocaleDateString("en-US", {
					year: "numeric",
					month: "short",
					day: "numeric",
				})
			: "NA";

	getGraphData = () =>
		this.state.history.map(_record => {
			if (!Object.keys(_record["data"]["statistics"]).includes(this.state.selectedStatOption)) {
				return {};
			}
			const overview = _record["data"]["statistics"][this.state.selectedStatOption]["numeric"].value;

			const mainName = _record["data"]["project_name"];
			const historyObject = {
				[mainName]: overview,
				date: this.beautifyDate(_record["date"] || "NA"),
			};
			for (const project of _record["data"]["includes"]) {
				historyObject[project["project_name"]] =
					project["statistics"][this.state.selectedStatOption]["numeric"].value;
			}
			return historyObject;
		});

	getLabels = () => {
		if (this.state.history.length === 0) {
			return [];
		}
		const names = [this.state.history[0]["data"]["project_name"]];
		this.state.history[0]["data"]["includes"].map(_record => {
			names.push(_record["project_name"]);
		});
		return names;
	};

	showGraph = graph => {
		this.setState({selectedGraph: graph});
	};

	render() {
		const projects = this.state.statistics;
		const chartColors = [
			properties.kbrColors.green,
			properties.kbrColors.vividCeruleanBlue,
			properties.kbrColors.yellow,
		];

		const labels = this.getLabels();
		const historyLabels = this.getLabels();

		return (
			<Spin spinning={this.state.loading}>
				<Row style={{margin: "10px"}}>
					<Col span={24}>
						<FullHeightCard>
							{this.tagsData().map(tag => (
								<CheckableTag
									key={tag}
									checked={this.state.selectedOption.value === tag.value}
									onChange={() => this.getStatistics(tag)}>
									{tag.label}
								</CheckableTag>
							))}
						</FullHeightCard>
					</Col>
				</Row>
				{projects.project_name && (
					<Row style={{margin: "10px"}}>
						<Col span={12}>
							<Row>
								<Col span={24}>
									<FullHeightCard title={`${projects.project_name}`}>
										<DashboardRowv3
											stats={projects.statistics}
											labels={this.state.labels}
											selectedRow={this.state.selectedStatOption}
										/>
									</FullHeightCard>
								</Col>
							</Row>
							<Row>
								{projects?.includes.length > 0 &&
									projects.includes.map((project, index) => (
										<Col span={12} key={index} style={{padding: "10px 0"}}>
											<FullHeightCard title={`${project.project_name}`}>
												<DashboardRowv3
													stats={project.statistics}
													labels={this.state.labels}
													selectedRow={this.state.selectedStatOption}
												/>
											</FullHeightCard>
										</Col>
									))}
							</Row>
						</Col>
						<Col span={12} style={{padding: "0px 0px 10px 10px"}}>
							<FullHeightCard>
								<Row style={{margin: "10px 0"}}>
									{this.graphsData().map(tag => (
										<CheckableTag
											key={tag}
											checked={this.state.selectedGraph.value === tag.value}
											onChange={() => this.showGraph(tag)}>
											{tag.label}
										</CheckableTag>
									))}
								</Row>
								<Row style={{margin: "10px 0"}}>
									{this.state.statOptions.map(tag => (
										<CheckableTag
											key={tag}
											checked={this.state.selectedStatOption === tag}
											onChange={() => this.setState({selectedStatOption: tag})}>
											{this.state.labels[tag] || tag}
										</CheckableTag>
									))}
								</Row>
								{this.state.selectedGraph.value === "pie" && (
									<Row>
										{projects?.includes.length > 0 && (
											<Col span={8}>
												<Statistic
													value={"Total"}
													valueStyle={{fontSize: "14px", fontWeight: "bold"}}
												/>
												<PieChart
													data={projects}
													field={"total"}
													option={this.state.selectedStatOption}
												/>
											</Col>
										)}
										{projects?.includes.length > 0 && (
											<Col span={8}>
												<Statistic
													value={"Stocked"}
													valueStyle={{fontSize: "14px", fontWeight: "bold"}}
												/>
												<PieChart
													data={projects}
													field={"stocked"}
													option={this.state.selectedStatOption}
												/>
											</Col>
										)}
										{projects?.includes.length > 0 && (
											<Col span={8}>
												<Statistic
													value={`Value (${projectCurrency})`}
													valueStyle={{fontSize: "14px", fontWeight: "bold"}}
												/>
												<PieChart
													data={projects}
													field={"value"}
													option={this.state.selectedStatOption}
												/>
											</Col>
										)}
									</Row>
								)}

								<Row style={{padding: "10px 0"}}>
									<Col span={24}>
										<Spin spinning={this.state.historyLoading}>
											<Graph
												colors={chartColors}
												labels={historyLabels}
												legendFields={labels}
												data={this.getGraphData()}
												height={250}
											/>
										</Spin>
									</Col>
								</Row>
							</FullHeightCard>
						</Col>
					</Row>
				)}
			</Spin>
		);
	}
}
